//-- Copyright (c) 2021 TMT Insights Group, LLC
import { LoadingStatuses, PAGE_SIZE } from '@/constants/constants';
import { createSelector } from '@reduxjs/toolkit';
import { HistoricTitleEntity, TITLE_HISTORY_DEFAULT_KEY, TitleHistoryBuckets, UserPreferences } from '../slices/slice_types';
import { RootState } from '../store';
import { selectHomePathId } from './fileManagerSelectors';
import { selectSelectedHierarchy } from './orderPlacementSelectors';

export const selectAuth = (state: RootState) => state.auth || {};
// export const selectAppLoading = (state) => !selectAuth(state)?.initFinished;
export const selectAppLoading = createSelector([selectAuth], (auth) => !auth.initFinished);
export const selectUser = createSelector([selectAuth], (auth) => auth.user || {});
export const selectUserId = createSelector([selectUser], (user) => user.id);
export const selectUserEmail = createSelector([selectUser], (user) => user.email && user.email.toLowerCase());
export const selectIsAuthenticated = createSelector([selectUserEmail], (userEmail) => !!userEmail);
export const selectUserPreferences = createSelector([selectAuth], (auth) => auth.preferences || {});
export const selectUserColor = createSelector(selectUser, (user) => user.color) || '#009FA8';
export const selectUserDateFormat = createSelector(selectUserPreferences, (prefs) =>
  prefs.date_format ? prefs.date_format.replace('mm', 'MM') : 'MM/dd/yyyy'
);
export const selectUserTimeFormat = createSelector(selectUserPreferences, (prefs) =>
  prefs.time_format && !prefs.time_format.includes('aa') ? prefs.time_format.replace('hh', 'HH') : 'hh:mm aa'
);

export const selectUserDarkMode = createSelector(selectUserPreferences, (prefs) => {
  if (typeof prefs.dark_mode === 'string' && prefs.dark_mode === '') {
    const userDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return userDarkMode;
  }
  return prefs.dark_mode;
});

export const selectUserPageSize = createSelector(selectUserPreferences, (prefs) => {
  if (!prefs?.page_size) {
    return PAGE_SIZE;
  }
  return prefs.page_size;
});

export const selectUserFullWidthMode = createSelector(selectUserPreferences, (prefs) => {
  if (!prefs?.full_width_mode || typeof prefs.full_width_mode === 'boolean') {
    return [];
  }

  return prefs.full_width_mode;
});

export const selectAuthLoading = createSelector([selectAuth], (auth) => auth.status === LoadingStatuses.pending);

// export const selectSavedSearches = (state: RootState): SavedSearches => {
//   const tableKey = state.dataTable.currentTable;
//   return selectUserPreferences(state)?.[tableKey]?.saved_searches || [];
// };

export const selectSavedSearches = createSelector([selectUserPreferences, (state: RootState) => state.dataTable.currentTable], (prefs, tableKey) => {
  return prefs?.[tableKey as keyof UserPreferences]?.saved_searches || [];
});

export const selectActiveModuleTitleHistoryBucket = createSelector(
  [selectUserPreferences, (state: RootState) => state.dataTable.currentModule],
  (prefs: Partial<UserPreferences>, currentModule: undefined | string | null): TitleHistoryBuckets => {
    return !currentModule ? {} : prefs?.titles?.history?.[currentModule] || {};
  }
);

export const selectActiveModuleTitleHistory = createSelector(
  [selectActiveModuleTitleHistoryBucket, selectSelectedHierarchy],
  (buckets: TitleHistoryBuckets, activeHierarchy: string | null | undefined): HistoricTitleEntity[] => {
    const bucketKey = activeHierarchy && activeHierarchy.length > 0 ? activeHierarchy : TITLE_HISTORY_DEFAULT_KEY;

    return buckets[bucketKey] ?? [];
  }
);

// read_all
// view_dashboard
// view_filemanager
// read_orders
// write_orders
// add_customer
// change_customer
// delete_customer
// read_jobs
// write_jobs
// read_tasks
// write_tasks
// read_profiles
// write_profiles
// view_titleentity

const checkPermissions = (permissions: string[], permission: string) => permissions.includes(permission);

// USER PERMISSION SELECTORS
export const selectPermissions = createSelector([selectAuth], (auth) => auth.permissions || []);

/**
 * IMPORTANT: Any use of these permissions requires you to add a line item to the following Confluence page:
 * https://tmtinsights.atlassian.net/wiki/spaces/TMT/pages/313262089/Polaris+Permissions
 *
 * This is necessary for us to track how features are managed via permissions within the app.
 */

// dashboard
export const selectCanViewDashboard = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'view_dashboard'));

// file manager
export const selectCanViewFileManager = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'view_filemanager'));
export const selectCanWriteFileManager = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_filemanager'));
export const selectCanUploadFileManager = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'add_fileoriginupload'));

export const selectCanViewMediaInfo = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'view_mediainfo'));
export const selectCanViewIQ = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'oms_core_vfs.can_view_iq'));

// orders
export const selectCanReadOrders = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_orders'));
export const selectCanWriteOrders = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_orders'));
export const selectCanDeleteDraftOrders = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'delete_draft_orders'));
export const selectCanAddCustomer = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'add_customer'));
export const selectCanChangeCustomer = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'change_customer'));
export const selectCanDeleteCustomer = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'delete_customer'));
export const selectCanCreateEmbargoedTitleOrderPending = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'can_create_embargoed_title_order_pending')
);
export const selectCanCreateEmbargoedTitleOrderProgress = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'can_create_embargoed_title_order_progress')
);

// jobs
export const selectCanReadJobs = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_jobs'));
export const selectCanWriteJobs = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_jobs'));

// tasks
export const selectCanReadTasks = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_tasks'));
export const selectCanWriteTasks = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_tasks'));


// profiles
export const selectCanReadProfiles = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_profiles'));
export const selectCanWriteProfiles = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_profiles'));
export const selectCanViewProfileHistory = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'view_profiles_history'));
export const selectCanDeleteProfiles = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'delete_profiles'));
export const selectCanExportProfiles = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'export_profiles'));

// titles
export const selectCanReadTitles = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'inventory.can_read_titles'));
export const selectCanViewInventory = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'inventory.can_view_title_inventory'));
export const selectCanReadInventory = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'inventory.can_read_title_inventory'));
export const selectCanDeleteInventoryFile = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'inventory.can_delete_inventory_file')
);
export const selectCanDownloadInventory = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'inventory.can_download_title_inventory')
);
export const selectCanDownloadBucketInventory = createSelector(
  [selectPermissions, selectHomePathId],
  (perms, id) => id && checkPermissions(perms, `file_origin.${id}.download_files`)
);

export const selectCanViewTitleCustomer = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'inventory.can_view_title_customers'));
export const selectCanReadTitleCustomer = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'inventory.can_read_title_customers'));
export const selectCanWriteTitleCustomer = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'inventory.can_write_title_customers')
);
export const selectCanViewAvailWindows = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_windowing_data'));

// pending assets
export const selectCanReadPendingAssets = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_order_job_assets'));
export const selectCanWritePendingAssets = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_order_job_assets'));
export const selectCanLinkNewAsset = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'write_order_job_assets'));

// ingest management
export const selectCanViewIngestManagement = (state: RootState) => checkPermissions(selectPermissions(state), 'view_fileingest');
export const selectCanIngestFiles = (state: RootState) => checkPermissions(selectPermissions(state), 'add_fileingest');
export const selectCanChangeIngestFile = (state: RootState) => checkPermissions(selectPermissions(state), 'change_fileingest');
export const selectCanDeleteIngestFile = (state: RootState) => checkPermissions(selectPermissions(state), 'delete_fileingest');
export const selectCanWriteIngestRecord = (state: RootState) => checkPermissions(selectPermissions(state), 'ingest_manager.can_write_ingest_record');
export const selectCanUploadIngestManager = (state: RootState) => checkPermissions(selectPermissions(state), 'ingest_manager.can_upload');

// reports
export const selectCanDownloadTasksReport = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'download_tasks_report'));
export const selectCanDownloadOrdersReport = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'download_orders_report'));
export const selectCanDownloadJobsReport = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'download_jobs_report'));
export const selectCanDownloadPendingAssetsReport = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'download_jobs_assets_report')
);

export const selectAppVersion = createSelector(selectAuth, (auth) => auth.appVersion);
export const selectSiteObject = createSelector(selectAuth, (auth) => auth.site);

// packaging / content_assembly
export const selectCanCreateGenericTemplates = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'read_all'));
export const selectCanAddAssemblyTemplates = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'add_assemblytemplate'));
export const selectCanViewContentAssemblyTasks = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'view_content_assembly_task'));
export const selectCanWriteContentAssemblyTasks = createSelector(selectPermissions, (perms) =>
  checkPermissions(perms, 'write_content_assembly_task')
);

// exceptions
export const selectCanViewExceptions = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'view_event'));
export const selectCanChangeExceptions = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'change_event'));

// timeZone
export const selectUserTimeZone = createSelector(selectAuth, (auth) => auth.timeZone || null);

// focus
export const selectCanViewAvails = createSelector(selectPermissions, (perms) => checkPermissions(perms, 'focus.can_view_avails'));
